import type { UserProgressEvent } from "../lib/runEvents";

// FriendlyNarration is the founder's single run-progress surface (0027):
// a vertical "path" of phase steps that extends as the run advances.
// Completed steps show a popped-in checkmark, the active step pulses, and
// each new step slides in from the left — a lightly gamified sense of
// motion without engine vocabulary or competing progress widgets.
//
// Each `user_progress` event is one step. Internal IDs / pipeline
// structure must NOT appear in `label` (T6 / SA-M5) — labels come from the
// orchestrator's SubPrompt.UserFacingLabel().
//
// `done`: when the run has finished, every step (including the last) shows
// the completed checkmark. While running, the last step is the active one.
// This is what makes the trail reach "fully done" — the terminal phase is
// never closed by a phase transition (there's nothing after it), so the
// run-complete signal is what finalizes it here.
export function FriendlyNarration({
  events,
  done = false,
}: {
  events: UserProgressEvent[];
  done?: boolean;
}) {
  if (events.length === 0) {
    return null;
  }
  const lastIndex = events.length - 1;
  return (
    <ol className="space-y-3">
      {events.map((e, i) => {
        const isCurrent = !done && i === lastIndex;
        const isLastRow = i === lastIndex;
        return (
          <li
            key={`${i}-${e.label}`}
            className="relative flex items-center gap-3 animate-step-in"
          >
            {/* Connector line linking this step to the next — draws the
                "path". Hidden on the last row. */}
            {!isLastRow && (
              <span
                aria-hidden="true"
                className="absolute left-[6px] top-[18px] h-[calc(100%+0.25rem)] w-px bg-ink-800"
              />
            )}
            <StepIcon current={isCurrent} />
            <span
              className={
                isCurrent
                  ? "text-[15px] leading-snug text-ink-100"
                  : "text-[15px] leading-snug text-ink-300"
              }
            >
              {e.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// StepIcon renders the active pulse or the completed checkmark. The two
// branches are distinct elements (the completed branch is keyed) so that
// when a step transitions current → done, React remounts the checkmark and
// replays the pop animation — the per-step reward beat.
function StepIcon({ current }: { current: boolean }) {
  if (current) {
    return (
      <span
        className="relative z-10 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center"
        aria-hidden="true"
      >
        <span className="absolute inline-flex h-3.5 w-3.5 rounded-full bg-accent opacity-40 animate-calm-pulse" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
      </span>
    );
  }
  return (
    <span
      key="done"
      className="relative z-10 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-ok-solid/20 text-ok animate-check-pop"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 12 12"
        className="h-2.5 w-2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2.5 6.5 L5 9 L9.5 3.5" />
      </svg>
    </span>
  );
}
